import { useEffect, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import { Typography } from '@mui/material';

import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import {
    ASSIGN_OFFER,
    ASSIGN_OFFER_PARTNER_OF_PARTNERS,
} from 'src/graphql/mutations/assignTerminalOffer.mutation';
import { DELETE_PRICING_OFFER } from 'src/graphql/mutations/deletePricingOffer.mutation';
import { GET_PRICING_OFFERS_BY_TYPE } from 'src/graphql/queries/pricing.query';
import { usePartnerAssignment } from 'src/hooks/usePartnerAssignment/usePartnerAssignment';

import { useAuthContext } from 'src/contexts/AuthProvider';
import { ProductStatusChip } from './productUtils';
import { filteredPricingOffersTableData, FilterOptionsType } from './tableUtils';

export const getFilterOptions = (offerData) => {
    const pricingOffers = offerData?.getPricingOffersByType?.data ?? [];
    return {
        'Partner Name': Array.from(new Set(pricingOffers.map(({ name }) => name))).map((name) => ({
            value: name,
            label: name,
        })),
        Status: Array.from(new Set(pricingOffers.map(({ status }) => status))).map((status) => ({
            value: status,
            label: status,
        })),
    } as FilterOptionsType;
};

export const getExportData = (offerData) => {
    let pricingOffers = offerData?.getPricingOffersByType?.data ?? [];

    pricingOffers = pricingOffers.map((el) => ({
        ...el,
        name: el.name,
        status: el.status,
        assignToTotal: (el.partners?.length ?? 0).toString(),
        partnerName: el.partners?.[0]?.business?.legalName ?? '',
        idPrefix: el.id.toString().padStart(4, '0'),
    }));

    return pricingOffers;
};

export const getTableData = (offerData, tableFilters, handleOpenAssignModal) => {
    const { isAdmin } = useAuthContext();

    return filteredPricingOffersTableData(
        offerData?.getPricingOffersByType?.data ?? [],
        tableFilters
    ).map((offer) => ({
        ...offer,
        name: offer.name,
        statusChip: <ProductStatusChip status={offer.status} />,
        assignToTotal: (
            <Typography
                onClick={() => {
                    isAdmin && handleOpenAssignModal(offer.id);
                }}
                sx={{
                    cursor: isAdmin ? 'pointer' : 'not-allowed',
                    textDecoration: isAdmin ? 'underline' : 'none',
                }}
            >
                {(
                    (offer.partners?.length ?? 0) + (offer.partnersOfPartner?.length ?? 0)
                ).toString()}
            </Typography>
        ),
        partnerName: offer.partners?.[0]?.business?.legalName ?? '',
        idPrefix: offer.id.toString().padStart(4, '0'),
    }));
};

export const useAssignPartners = ({
    idToAssign,
    setIdToAssign,
    assignedPartners,
    assignedPartnersOfPartners,
    refetch,
    assignOfferForPartners,
    assignOfferForPartnersOfPartner,
    useTransactionOfferId,
}) => {
    return usePartnerAssignment({
        idToAssign,
        setIdToAssign,
        assignedPartners,
        assignedPartnersOfPartners,
        refetch,
        assignOfferForPartners,
        assignOfferForPartnersOfPartner,
        useTransactionOfferId,
    });
};

export const useAssignPartnersEffect = (
    idToAssign,
    offerData,
    setAssignedPartners,
    setAssignedPartnersOfPartners
) => {
    useEffect(() => {
        if (idToAssign) {
            const offer = (offerData?.getPricingOffersByType?.data ?? []).find(
                (el) => el.id === idToAssign
            );
            const assignedPartners = (offer?.partners || []).map((partner) => ({
                value: partner.id,
                type: 'partner',
            }));
            const assignedPartnersOfPartners = (offer?.partnersOfPartner ?? []).map(
                (partnerOfPartner) => ({
                    value: partnerOfPartner.id,
                    type: 'partnerOfPartner',
                })
            );
            setAssignedPartners(assignedPartners);
            setAssignedPartnersOfPartners(assignedPartnersOfPartners);
        }
    }, [idToAssign, offerData, setAssignedPartners, setAssignedPartnersOfPartners]);
};

export const usePricingOffersState = (pricingType) => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number>();
    const [idToAssign, setIdToAssign] = useState<number>();
    const [offerData, setOfferData] = useState([]);
    const [tableFilters, setTableFilters] = useState({});
    const [assignedPartners, setAssignedPartners] = useState([]);
    const [assignedPartnersOfPartners, setAssignedPartnersOfPartners] = useState([]);

    const useTransactionOfferId = false;

    const [getOfferData, { loading, refetch }] = useLazyQuery(GET_PRICING_OFFERS_BY_TYPE, {
        onError: (error) => {
            showErrorToast(error.message);
        },
        onCompleted: (offerData) => {
            setOfferData(offerData);
        },
    });

    useEffect(() => {
        const variables = {
            input: { type: pricingType },
            page: { page: 0, pageSize: 5, search: '' },
        };
        getOfferData({ variables });
    }, [getOfferData, pricingType]);

    return {
        openDeleteModal,
        setOpenDeleteModal,
        idToDelete,
        setIdToDelete,
        idToAssign,
        setIdToAssign,
        offerData,
        tableFilters,
        setTableFilters,
        assignedPartners,
        setAssignedPartners,
        assignedPartnersOfPartners,
        setAssignedPartnersOfPartners,
        useTransactionOfferId,
        getOfferData,
        loading,
        refetch,
    };
};

export const useDeleteOffer = (refetch) => {
    return useMutation(DELETE_PRICING_OFFER, {
        onError: () => showErrorToast('We were unable to delete the selected offer'),
        onCompleted: () => {
            showSuccessToast('Offer Deleted!');
            refetch();
        },
    });
};

export const useAssignOfferForPartners = (refetch) => {
    return useMutation(ASSIGN_OFFER, {
        onError: () => showErrorToast('Assignment request was unsuccessful'),
        onCompleted: () => {
            showSuccessToast('Partner allocation was successful!');
            refetch();
        },
    });
};

export const useAssignOfferForPartnersOfPartner = (refetch) => {
    return useMutation(ASSIGN_OFFER_PARTNER_OF_PARTNERS, {
        onError: () => showErrorToast('Assignment request was unsuccessful'),
        onCompleted: () => {
            showSuccessToast('Partner of partner allocation was successful!');
            refetch();
        },
    });
};
